import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Custom hook that allows for a state value to be set and executed with a callback function.
 * @param initialState The initial state value.
 * @returns An array containing the state value and a function to set the state value.
 */
export const useStateWithCallback = <T>(
  initialState: T
): [state: T, setState: (updatedState: React.SetStateAction<T>, callback?: (updatedState: T) => void) => void] => {
  const [state, setState] = useState<T>(initialState)
  const callbackRef = useRef<(updated: T) => void>()

  const handleSetState = useCallback((updatedState: React.SetStateAction<T>, callback?: (updatedState: T) => void) => {
    callbackRef.current = callback
    setState(updatedState)
  }, [])

  useEffect(() => {
    if (typeof callbackRef.current === "function") {
      callbackRef.current(state)
      callbackRef.current = undefined
    }
  }, [state])

  return [state, handleSetState]
}
